<script lang="ts">
    import type { Deal, SocialProof, TileImage } from '@shared/types/types';
    import DealBadge from '@tile/components/product-image/DealBadge.svelte';
    import ProductImage from '@tile/components/product-image/ProductImage.svelte';
    import type { ImageAspectRatio, ImageLoadingOptions } from '@tile/components/product-image/types';
    import SocialProofBadges from '@tile/components/social-proof-badges/SocialProofBadges.svelte';

    interface Props {
        images: TileImage[];
        fallbackImage: TileImage;
        variationId: string;
        detailPageLink: string;
        imageLoadingOptions: ImageLoadingOptions;
        onClick?: (event: Event) => void;
        onImageChange?: () => void;
        deal?: Deal;
        aspectRatio?: ImageAspectRatio;
        rule?: string;
        brand?: string;
        size?: '50' | '100';
        hidePaginationDots?: boolean;
        title?: string;
        socialProof?: SocialProof;
        shouldDim?: boolean;
    }

    let {
        images,
        fallbackImage,
        variationId,
        detailPageLink,
        imageLoadingOptions,
        onClick,
        onImageChange,
        deal,
        aspectRatio,
        socialProof,
        rule,
        size = '100',
        hidePaginationDots = false,
        brand = '',
        title = '',
        shouldDim,
    }: Props = $props();

    let _activeSlideIndex = $state(0);

    const showPaginationDots = $derived(!hidePaginationDots && images.length > 1);

    const mappedImages = $derived(
        images.length === 0
            ? [fallbackImage]
            : images.map(
                  (img): TileImage => ({
                      id: img.id ?? fallbackImage.id,
                      jpeg: img.jpeg ?? fallbackImage.jpeg,
                      webp: img.webp ?? fallbackImage.webp,
                      alt: img.alt ?? fallbackImage.alt,
                      fallback: img.fallback ?? fallbackImage.fallback,
                  }),
              ),
    );
</script>

{#key variationId}
    <div class="reptile-product-image-carousel__wrapper">
        <oc-carousel-v1
            class="reptile-product-image-carousel"
            oc-aria-label="Bildergalerie für {brand} {title}. Zum Wechseln der Ansicht Navigationselemente oder Wischgesten verwenden."
            arrow-buttons-on-hover
            hide-pagination-dots={hidePaginationDots || undefined}
            {size}
            onoc-item-visibility-changed={(e) => {
                if (!e.detail?.stageIsScrolling) {
                    _activeSlideIndex = e.detail?.visibleItem?.index ?? 0;
                    onImageChange?.();
                }
            }}
        >
            {#each mappedImages as image, index}
                <ProductImage
                    {image}
                    imageLoadingOptions={index === 0 ? imageLoadingOptions : { loading: 'lazy', fetchpriority: 'auto' }}
                    {variationId}
                    {detailPageLink}
                    {onClick}
                    {aspectRatio}
                    shouldDim={index === 0 && shouldDim}
                />
            {/each}
        </oc-carousel-v1>
        {#if deal}
            <DealBadge {deal} {variationId} {rule} />
        {/if}
        {#if socialProof && Object.values(socialProof).some(Boolean)}
            <div
                class="reptile-product-image-carousel__social-proof-badges"
                class:reptile-product-image-carousel__social-proof-badges--with-dots={showPaginationDots}
            >
                <SocialProofBadges {socialProof} />
            </div>
        {/if}
    </div>
{/key}

<style lang="scss">
    @use '@otto-ec/design-tokens/tokens';

    .reptile-product-image-carousel {
        &__wrapper {
            position: relative;
        }

        &__social-proof-badges {
            position: absolute;
            bottom: 0;
            left: 0;
            margin-left: 10px;
            margin-bottom: tokens.$oc-semantic-spacing-25;

            &--with-dots {
                margin-bottom: tokens.$oc-semantic-spacing-100;
            }
        }
    }
</style>
